// CJK text: writing Chinese, Japanese and Korean into a PDF.
//
// Everything else in this editor writes text as WinAnsi bytes in a simple
// font — 256 glyphs, one byte each. CJK cannot fit that, so it needs a
// composite (Type0) font, and how this gets one is worth recording
// because the alternatives all fail:
//
//   - Embedding a real CJK face is out: Noto Sans CJK is a 32 MB file,
//     and mupdf's wasm `subsetFonts()` drops CID font files rather than
//     shrinking them (measured). A 30 MB export is not a feature.
//   - `addSimpleFont` cannot express more than 256 codes.
//   - mupdf's own FreeText appearance generator only speaks Base-14.
//
// What works — verified end to end — is `PDFDocument.addCJKFont(font,
// lang, 0, serif)`. It writes a Type0 font with one of the standard Adobe
// orderings (GB1 / CNS1 / Japan1 / Korea1) and a `Uni…-UTF16-H` encoding,
// embedding NOTHING: the reader substitutes its own CJK face, which every
// mainstream reader ships. The whole thing costs about 700 bytes per
// script. Strings are then plain UTF-16BE.
//
// Metrics: those fonts carry no /W array and no /DW, so the PDF default
// of 1000 applies and EVERY glyph is exactly one em wide. That is also
// true of the faces readers substitute, because these orderings are
// full-width by construction. Proportional Latin therefore has to stay
// in a Latin font, which is what the run splitting below is for.

/** The four Adobe orderings, named as mupdf's `addCJKFont` wants them. */
export type CjkScript = "zh-Hans" | "zh-Hant" | "ja" | "ko";

export const CJK_SCRIPTS: readonly { id: CjkScript; label: string }[] = [
  { id: "zh-Hans", label: "Chinese (Simplified)" },
  { id: "zh-Hant", label: "Chinese (Traditional)" },
  { id: "ja", label: "Japanese" },
  { id: "ko", label: "Korean" },
];

/** One stretch of text that can be drawn with a single font.
 *  `script: null` means "the Latin font this caller was already using". */
export interface TextRun {
  text: string;
  script: CjkScript | null;
}

const inRange = (c: number, lo: number, hi: number) => c >= lo && c <= hi;

/** Kana — unambiguously Japanese. */
function isKana(c: number): boolean {
  return (
    inRange(c, 0x3040, 0x30ff) || // hiragana + katakana
    inRange(c, 0x31f0, 0x31ff) || // katakana phonetic extensions
    inRange(c, 0xff66, 0xff9d) // halfwidth katakana
  );
}

/** Hangul — unambiguously Korean. */
function isHangul(c: number): boolean {
  return (
    inRange(c, 0xac00, 0xd7a3) || // syllables
    inRange(c, 0x1100, 0x11ff) || // jamo
    inRange(c, 0x3130, 0x318f) || // compatibility jamo
    inRange(c, 0xffa0, 0xffdc) // halfwidth jamo
  );
}

/** Han ideographs. Shared by all four scripts, so they say nothing about
 *  WHICH — the caller's preference decides. */
function isHan(c: number): boolean {
  return (
    inRange(c, 0x4e00, 0x9fff) || // unified
    inRange(c, 0x3400, 0x4dbf) || // extension A
    inRange(c, 0xf900, 0xfaff) || // compatibility
    inRange(c, 0x20000, 0x3ffff) // extensions B and beyond
  );
}

/** CJK punctuation and fullwidth forms: wide, and script-neutral. */
function isCjkPunctuation(c: number): boolean {
  return (
    inRange(c, 0x3000, 0x303f) || // 。、「」etc.
    inRange(c, 0xff01, 0xff60) || // fullwidth ASCII + brackets
    inRange(c, 0xfe30, 0xfe4f) // CJK compatibility forms
  );
}

/** Does this character need a CJK font? */
export function isCjkChar(codePoint: number): boolean {
  return (
    isKana(codePoint) ||
    isHangul(codePoint) ||
    isHan(codePoint) ||
    isCjkPunctuation(codePoint)
  );
}

/** Does this text contain anything a WinAnsi simple font can't draw? */
export function hasCjk(text: string): boolean {
  for (const ch of text) {
    if (isCjkChar(ch.codePointAt(0)!)) return true;
  }
  return false;
}

/** The script a character names outright, or null if it doesn't. Han and
 *  CJK punctuation are deliberately null — they are used by all four. */
export function scriptOfChar(codePoint: number): CjkScript | null {
  if (isKana(codePoint)) return "ja";
  if (isHangul(codePoint)) return "ko";
  return null;
}

/** Guess the script for a whole string from the characters that can only
 *  belong to one. With neither kana nor hangul the caller's default
 *  stands — Han alone genuinely cannot be told apart, since the same
 *  ideographs are written in all four scripts. */
export function detectScript(text: string, fallback: CjkScript = "zh-Hans"): CjkScript {
  let ja = 0;
  let ko = 0;
  for (const ch of text) {
    const s = scriptOfChar(ch.codePointAt(0)!);
    if (s === "ja") ja += 1;
    else if (s === "ko") ko += 1;
  }
  if (ja === 0 && ko === 0) return fallback;
  return ja >= ko ? "ja" : "ko";
}

/** Split text into runs that can each be drawn with one font.
 *
 *  Latin (and anything else WinAnsi can represent) stays in the caller's
 *  font so it keeps its proportional metrics; CJK goes to the script
 *  font. Characters that name a script win over `preferred`, so a
 *  Japanese quotation inside Chinese prose still gets Japanese kana. */
export function splitRuns(text: string, preferred: CjkScript = "zh-Hans"): TextRun[] {
  const runs: TextRun[] = [];
  for (const ch of text) {
    const code = ch.codePointAt(0)!;
    const script = isCjkChar(code) ? (scriptOfChar(code) ?? preferred) : null;
    const last = runs[runs.length - 1];
    if (last && last.script === script) last.text += ch;
    else runs.push({ text: ch, script });
  }
  return runs;
}

/** A UTF-16BE hex string — the body of a PDF `<…>` string for a
 *  Uni…-UTF16-H encoded font. Astral characters become a surrogate pair,
 *  which the CMap maps to one CID (so they are ONE glyph wide). */
export function utf16beHex(text: string): string {
  let out = "";
  for (const ch of text) {
    const cp = ch.codePointAt(0)!;
    if (cp > 0xffff) {
      const v = cp - 0x10000;
      out += (0xd800 | (v >> 10)).toString(16).padStart(4, "0");
      out += (0xdc00 | (v & 0x3ff)).toString(16).padStart(4, "0");
    } else {
      out += cp.toString(16).padStart(4, "0");
    }
  }
  return out;
}

/** Width of a CJK run in ems: one per character, per the fonts' /DW
 *  default of 1000. Counts CHARACTERS, not UTF-16 code units — a
 *  surrogate pair is a single glyph. */
export function cjkWidthEm(text: string): number {
  let n = 0;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  for (const _ of text) n += 1;
  return n;
}

/** Characters that may not start a line (closing brackets, the small
 *  kana, sentence-final punctuation) — "kinsoku shori", the CJK
 *  line-breaking rules every typesetter applies. */
const NO_BREAK_BEFORE = new Set([
  ...'、。，．：；！？）］｝」』〕〉》”’ー々ぁぃぅぇぉっゃゅょゎァィゥェォッャュョヮヵヶ%‰°′″℃',
]);
/** …and characters that may not end one. */
const NO_BREAK_AFTER = new Set([...'（［｛「『〔〈《“‘¥￥＄$#＃']);

/** May a line break fall between these two characters?
 *
 *  Chinese and Japanese wrap between almost any two characters rather
 *  than at spaces, so a paragraph with no spaces in it must still wrap.
 *  Korean uses spaces like a Latin script, so hangul is left to ordinary
 *  word wrapping. */
export function canBreakBetween(prev: string, next: string): boolean {
  if (!prev || !next) return false;
  if (NO_BREAK_BEFORE.has(next)) return false;
  if (NO_BREAK_AFTER.has(prev)) return false;
  const a = prev.codePointAt(0)!;
  const b = next.codePointAt(0)!;
  // At least one side must be a wide character: Latin words keep their
  // own word-level wrapping.
  return (isCjkChar(a) && !isHangul(a)) || (isCjkChar(b) && !isHangul(b));
}
